import { useMemo } from "react"
import { Clip } from "../types"

interface LocalStorageConfig {
  storageKey?: string
}

/**
 * Hook-based LocalStorage backend for clipboard operations
 * Provides persistent storage that survives page reloads
 * Uses custom events for real-time synchronization between instances
 */
export const useLocalStorageBackend = (config: LocalStorageConfig = {}) => {
  try {
    const { storageKey = "pathwright_clipboard" } = config

    return useMemo(() => {
      const eventName = `clipboard:localStorage:${storageKey}`

      // Track subscribers for this instance
      const subscribers = new Set<(clips: Clip[]) => void>()

      const loadClipsFromStorage = (): Clip[] => {
        try {
          const stored = localStorage.getItem(storageKey)
          return stored ? JSON.parse(stored) : []
        } catch (error) {
          console.warn(
            `Failed to load clips from localStorage (key: ${storageKey}):`,
            error
          )
          return []
        }
      }

      const notifyOtherInstances = (clips: Clip[]) => {
        // Dispatch custom event to notify other instances
        const event = new CustomEvent(eventName, {
          detail: { clips, storageKey }
        })
        window.dispatchEvent(event)
      }

      const notifyCurrentInstance = (clips: Clip[]) => {
        // Notify subscribers of this instance
        subscribers.forEach((callback) => callback(clips))
      }

      return {
        isAvailable: () => {
          return (
            typeof window !== "undefined" && window.localStorage !== undefined
          )
        },

        subscribeToClips: (callback: (clips: Clip[]) => void) => {
          // Initial load
          const initialClips = loadClipsFromStorage()
          callback(initialClips)

          // Add to subscribers
          subscribers.add(callback)

          // Listen for changes from other instances
          const handleStorageChange = (event: CustomEvent) => {
            if (event.detail?.storageKey === storageKey) {
              callback(event.detail.clips)
            }
          }

          // Listen for localStorage changes (from other tabs/windows)
          const handleStorageEvent = (event: StorageEvent) => {
            if (event.key === storageKey && event.newValue) {
              try {
                const clips = JSON.parse(event.newValue)
                callback(clips)
              } catch (error) {
                console.warn(
                  `Failed to parse localStorage update (key: ${storageKey}):`,
                  error
                )
              }
            }
          }

          window.addEventListener(
            eventName,
            handleStorageChange as EventListener
          )
          window.addEventListener("storage", handleStorageEvent)

          // Return unsubscribe function
          return () => {
            subscribers.delete(callback)
            window.removeEventListener(
              eventName,
              handleStorageChange as EventListener
            )
            window.removeEventListener("storage", handleStorageEvent)
          }
        },

        saveClips: async (clips: Clip[]): Promise<void> => {
          try {
            localStorage.setItem(storageKey, JSON.stringify(clips))
            // Notify current instance immediately
            notifyCurrentInstance(clips)
            // Notify other instances about the change
            notifyOtherInstances(clips)
          } catch (error) {
            console.warn(
              `Failed to save clips to localStorage (key: ${storageKey}):`,
              error
            )
            throw error
          }
        },

        getName: () => "LocalStorageBackend"
      }
    }, [storageKey])
  } catch (error) {
    // Log the error for debugging
    console.warn(
      "[Clipboard] LocalStorage backend failed to initialize:",
      error
    )

    // Return a backend that is always unavailable
    return {
      isAvailable: () => false,
      subscribeToClips: (callback: (clips: Clip[]) => void) => {
        // Return empty clips and no-op unsubscribe
        callback([])
        return () => {}
      },
      saveClips: async (clips: Clip[]): Promise<void> => {
        // Silently fail - this backend is not available
        console.warn(
          "[Clipboard] LocalStorage backend is unavailable, save operation ignored"
        )
      },
      getName: () => "LocalStorageBackend (Unavailable)"
    }
  }
}

export default useLocalStorageBackend
